import { useEffect, useRef } from 'react';
import { useParams, Link as RouterLink, Navigate } from 'react-router-dom';
import { useTranslations } from '../context/TranslationContext';
import { Header } from '../components/Header';
import { useReveal } from '../hooks/useReveal';
import { useReadingProgress } from '../hooks/useReadingProgress';
import {
  getArticleBySlug,
  getRelatedArticles,
  toArticleLang,
  findArticleInAnyLanguage,
} from '../services/articles';
import { ArrowRight } from 'lucide-react';
import { ArticleBlock, ArticleLang } from '../types/article';
import { SeoHead } from '../components/SeoHead';
import {
  getArticleMeta,
  articlePath,
  articlesListPath,
  homePath,
  buildArticleSchema,
  buildBreadcrumbSchema,
} from '../services/seo';

const renderBlock = (block: ArticleBlock, index: number) => {
  if (block.type === 'h2') {
    return (
      <h2 key={index} className="display text-[26px] md:text-[32px] mt-12 mb-5" data-reveal>
        {block.text}
      </h2>
    );
  }
  if (block.type === 'ul') {
    return (
      <ul key={index} className="space-y-3 mb-6" data-reveal>
        {block.items?.map((item, i) => (
          <li key={i} className="flex gap-4 text-ink text-base leading-[1.8] font-light">
            <span className="text-accent">—</span>
            <span>{item}</span>
          </li>
        ))}
      </ul>
    );
  }
  return (
    <p key={index} className="text-ink text-base leading-[1.8] font-light mb-6" data-reveal>
      {block.text}
    </p>
  );
};

const Article = () => {
  const { slug } = useParams<{ slug: string }>();
  const { t, lang } = useTranslations();
  const articleLang = toArticleLang(lang);
  const mainRef = useRef<HTMLDivElement>(null);
  const strings = t.articlePage;

  const readingProgress = useReadingProgress();

  useReveal(mainRef);

  useEffect(() => {
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
  }, [slug]);

  const article = slug ? getArticleBySlug(slug, articleLang) : undefined;
  const homeUrl = lang === 'hu' ? '/hu' : `/${lang}`;
  const articlesUrl = `/${lang}/articles`;

  // An unprefixed URL (/articles/<slug>) resolves its language from the
  // hostname, which can disagree with the slug — send it to the canonical
  // language-prefixed URL rather than reporting the article missing.
  if (!article && slug) {
    const elsewhere = findArticleInAnyLanguage(slug);
    if (elsewhere) {
      return <Navigate to={`/${elsewhere.lang}/articles/${elsewhere.slug}`} replace />;
    }
  }

  if (!article) {
    return (
      <div className="min-h-screen bg-cream" ref={mainRef}>
        <SeoHead
          lang={articleLang}
          title={strings.notFoundTitle}
          description={strings.notFoundBody}
          path={slug ? articlePath(articleLang, slug) : articlesListPath(articleLang)}
          alternates={{
            [articleLang]: slug ? articlePath(articleLang, slug) : articlesListPath(articleLang),
          } as Partial<Record<ArticleLang, string>>}
        />
        <Header variant="page" />
        <div className="pt-40 pb-32 px-8 lg:px-16 max-w-3xl mx-auto text-center" data-reveal>
          <div className="eyebrow mb-6">{t.articles.title}</div>
          <h1 className="display text-[40px] md:text-[52px] mb-6">{strings.notFoundTitle}</h1>
          <p className="text-mute text-base leading-[1.8] font-light mb-10">{strings.notFoundBody}</p>
          <RouterLink to={articlesUrl} className="arrow-link">
            <span>{strings.notFoundLink}</span>
            <ArrowRight size={16} strokeWidth={1.5} />
          </RouterLink>
        </div>
      </div>
    );
  }

  const related = getRelatedArticles(article, articleLang);
  const practiceAreaLabel =
    (strings.practiceAreas as Record<string, string>)[article.practiceArea] ?? article.practiceArea;

  const { title: metaTitle, description: metaDescription } = getArticleMeta(articleLang, article, t);
  const alternates: Partial<Record<ArticleLang, string>> = {
    [articleLang]: articlePath(articleLang, article.slug),
  };
  Object.entries(article.translations).forEach(([transLang, transSlug]) => {
    alternates[transLang as ArticleLang] = articlePath(transLang as ArticleLang, transSlug as string);
  });

  return (
    <div className="min-h-screen bg-cream animate-fadeIn" ref={mainRef}>
      {/* Reading progress — scaleX avoids laying out on every scroll frame */}
      <div
        className="reading-progress w-full"
        style={{ transform: `scaleX(${readingProgress})` }}
        aria-hidden="true"
      />
      <SeoHead
        lang={articleLang}
        title={metaTitle}
        description={metaDescription}
        path={articlePath(articleLang, article.slug)}
        alternates={alternates}
        type="article"
        jsonLd={[
          buildArticleSchema(articleLang, article, practiceAreaLabel),
          buildBreadcrumbSchema([
            { name: t.page.navigation.home, path: homePath(articleLang) },
            { name: t.articles.title, path: articlesListPath(articleLang) },
            { name: article.title, path: articlePath(articleLang, article.slug) },
          ]),
        ]}
      />
      <Header variant="page" />

      <article className="pt-40 pb-24 px-8 lg:px-16 max-w-3xl mx-auto">
        <div className="mb-10" data-reveal>
          <div className="eyebrow mb-5 flex flex-wrap items-center gap-x-3 gap-y-1">
            <span>{practiceAreaLabel}</span>
            <span aria-hidden="true">·</span>
            <span>
              {article.readTime} {strings.readTimeSuffix}
            </span>
            <span aria-hidden="true">·</span>
            <span>{article.date}</span>
          </div>
          <h1 className="display text-[38px] md:text-[52px] leading-[1.08] mb-6">{article.title}</h1>
          <p className="text-mute text-lg leading-[1.7] font-light max-w-2xl">{article.description}</p>
        </div>

        <div className="hairline mb-12" />

        <div>{article.blocks.map(renderBlock)}</div>

        {related.length > 0 && (
          <div className="mt-20 pt-12 border-t border-hair" data-reveal>
            <div className="eyebrow mb-8">{strings.relatedHeading}</div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {related.map((relatedArticle) => (
                <RouterLink
                  key={relatedArticle.slug}
                  to={`/${lang}/articles/${relatedArticle.slug}`}
                  className="group block hairline pb-8"
                >
                  <div className="text-[12px] tracking-[0.1em] uppercase text-mute mb-3">
                    {(strings.practiceAreas as Record<string, string>)[relatedArticle.practiceArea] ??
                      relatedArticle.practiceArea}{' '}
                    · {relatedArticle.readTime} {strings.readTimeSuffix}
                  </div>
                  <h3 className="display text-[22px] leading-[1.2] mb-2 link-grow">{relatedArticle.title}</h3>
                  <p className="text-mute text-sm leading-[1.7] font-light">{relatedArticle.description}</p>
                </RouterLink>
              ))}
            </div>
          </div>
        )}

        <div className="mt-20 pt-12 border-t border-hair text-center" data-reveal>
          <h2 className="display text-[28px] md:text-[34px] mb-4">{strings.ctaHeading}</h2>
          <p className="text-mute text-base leading-[1.8] font-light max-w-xl mx-auto mb-8">
            {strings.ctaBody}
          </p>
          <RouterLink to={`${homeUrl}#contact`} className="btn-primary">
            <span>{strings.ctaButton}</span>
            <ArrowRight size={16} strokeWidth={1.5} />
          </RouterLink>
        </div>
      </article>

      <footer className="border-t border-hair py-16 bg-cream">
        <div className="max-w-7xl mx-auto px-8 lg:px-16 text-center">
          <p className="text-mute text-sm font-light">{t.articles.footerNote}</p>
        </div>
      </footer>
    </div>
  );
};

export default Article;
